// firebaseConfig.js
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
  setPersistence, 
  browserLocalPersistence,
  updateProfile,
  signInWithPhoneNumber,
  RecaptchaVerifier, 
  linkWithPhoneNumber,
} from "firebase/auth";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  addDoc, 
  getDoc, 
  getDocs, 
  collection, 
  updateDoc, 
  query, 
  where, 
  onSnapshot,
  arrayUnion,
  Timestamp,
  serverTimestamp as fServerTimestamp,
  writeBatch,
  orderBy,
} from "firebase/firestore"; // Import Firestore
import { 
  getStorage, 
  getDownloadURL, 
  uploadBytes,
  uploadBytesResumable, 
  ref as sRef
} from "firebase/storage"; // Import Storage
// Track user activity using Firebase Analytics
import { 
  getAnalytics, 
  logEvent 
} from "firebase/analytics";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { getDatabase, ref as dbRef, set, push, onValue, serverTimestamp as dbServerTimestamp  } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";


let firebaseConfig;

if (import.meta.env.NODE_ENV === 'production') {
  // Your web app's Firebase configuration
  firebaseConfig = {
    apiKey: import.meta.env.VITE_API_FIRESTORE_KEY || 'mock_key',
    authDomain: import.meta.env.VITE_API_FIRESTORE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_API_FIRESTORE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_API_FIRESTORE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_API_FIRESTORE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_API_FIRESTORE_APP_ID,
    measurementId: import.meta.env.VITE_API_FIRESTORE_MEASUREMENT_ID
  };
} else if (import.meta.env.NODE_ENV === 'staging') {
  // Your web app's Firebase configuration
  firebaseConfig = {
    apiKey: import.meta.env.VITE_API_FIRESTORE_KEY || 'mock_key',
    authDomain: import.meta.env.VITE_API_FIRESTORE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_API_FIRESTORE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_API_FIRESTORE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_API_FIRESTORE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_API_FIRESTORE_APP_ID,
    measurementId: import.meta.env.VITE_API_FIRESTORE_MEASUREMENT_ID
  };
} else if (import.meta.env.NODE_ENV === 'development') {
  // Your web app's Firebase configuration
  firebaseConfig = {
    apiKey: import.meta.env.VITE_API_FIRESTORE_KEY || 'mock_key',
    authDomain: import.meta.env.VITE_API_FIRESTORE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_API_FIRESTORE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_API_FIRESTORE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_API_FIRESTORE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_API_FIRESTORE_APP_ID,
    measurementId: import.meta.env.VITE_API_FIRESTORE_MEASUREMENT_ID
  };
  //auth.settings.appVerificationDisabledForTesting = true; // Disable App Verification in Development 
} else {
  // Your web app's Firebase configuration
  firebaseConfig = {
    apiKey: import.meta.env.VITE_API_FIRESTORE_KEY || 'mock_key',
    authDomain: import.meta.env.VITE_API_FIRESTORE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_API_FIRESTORE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_API_FIRESTORE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_API_FIRESTORE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_API_FIRESTORE_APP_ID,
    measurementId: import.meta.env.VITE_API_FIRESTORE_MEASUREMENT_ID
  };
}

// ✅ **Initialize Firebase Services**
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const firestore = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();
const messaging = getMessaging(app);
const database = getDatabase(app);
const functions = getFunctions(app);


// ✅ **Prevent Analytics Error in Non-Browser Environments**
let analytics = null;
if (typeof window !== "undefined") {
  analytics = getAnalytics(app);
}

// ✅ Register the service worker
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/firebase-messaging-sw.js")
    .then((registration) => {
      console.log("Service Worker registered with scope:", registration.scope);
    })
    .catch((err) => {
      console.error("Service Worker registration failed:", err);
    });
} else {
  console.warn("Service Worker is not supported in this browser.");
}


export { 
  auth, 
  firestore, 
  storage, 
  googleProvider, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  doc, 
  setDoc,
  addDoc,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  getDoc,
  getDocs,
  collection,
  query, 
  where,
  updateDoc,
  getDownloadURL, 
  uploadBytes,
  sendPasswordResetEmail,
  setPersistence, 
  browserLocalPersistence,
  analytics,
  messaging,
  logEvent,
  onSnapshot,
  database,
  dbRef,
  sRef,
  set,
  push,
  onValue,
  dbServerTimestamp,
  getToken,
  arrayUnion,
  uploadBytesResumable,
  Timestamp,
  updateProfile,
  fServerTimestamp,
  onMessage,
  functions,
  httpsCallable,
  signInWithPhoneNumber,
  RecaptchaVerifier, 
  linkWithPhoneNumber,
  writeBatch,
  orderBy,
};